/**
 * AuiGrid
 *
 * Responsive CSS grid layout component for AUI surfaces.
 * Arranges children in a responsive grid: 1 column on mobile,
 * scaling up to the specified max columns on wider screens.
 *
 * When children include DelegatedTaskCards, sorts them by status:
 * pending/running first, then completed/failed/cancelled.
 * When over capacity, oldest terminal cards are dropped to make
 * room for active cards.
 *
 * Props:
 *   columns (number, default 2) — max columns at widest breakpoint
 *   gap (number, default 4) — gap multiplier (Tailwind spacing scale)
 *   maxRows (number, default 4) — max rows before terminal cards get recycled
 */

import { useMemo, Children, type ReactElement } from 'react';
import { AnimatePresence } from 'motion/react';
import { AnimatedListItem } from '../../ui/AnimatedListItem';
import type { AuiRendererProps } from '../registry';
import type { DelegatedTaskStatus } from '../../../types/delegated-tasks';
import { delegatedTaskStatusRegistry } from '../../../hooks/useDelegatedTaskStatus';

const COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

const STATUS_PRIORITY: Record<DelegatedTaskStatus, number> = {
  pending: 0,
  running: 1,
  completed: 2,
  failed: 2,
  cancelled: 2,
};

interface IndexedChild {
  child: ReactElement;
  priority: number;
  index: number;
}

export function AuiGrid({ node, children, childNodes }: AuiRendererProps) {
  const props = node.component.props ?? {};
  const columns = Math.min(Math.max(typeof props.columns === 'number' ? props.columns : 2, 1), 4);
  const rawGap = props.gap;
  const gap = typeof rawGap === 'number' ? rawGap : 4;
  const maxRows = typeof props.maxRows === 'number' ? props.maxRows : 4;
  const maxCards = columns * maxRows;

  const visibleChildren = useMemo(() => {
    const childArray = Children.toArray(children) as ReactElement[];
    if (!childNodes || childNodes.length === 0) return childArray;

    // Pair children with their node IDs and filter out missing nodes
    const childIds = node.component.children ?? [];
    const validChildIds = new Set(childNodes.map((n) => n.id));
    const paired = childArray
      .map((child, i) => ({ child, nodeId: childIds[i] }))
      .filter(({ nodeId }) => nodeId && validChildIds.has(nodeId));

    const hasDelegatedCards = childNodes.some((n) => n.component.type === 'DelegatedTaskCard');
    if (!hasDelegatedCards) return paired.map(({ child }) => child);

    // Build indexed list with status info
    const indexed: IndexedChild[] = paired.map(({ child, nodeId }, i) => {
      const childNode = childNodes?.find((n) => n.id === nodeId);
      const delegatedRunId = childNode?.component.props?.delegatedRunId as string | undefined;
      const specStatus = (childNode?.component.props?.status as DelegatedTaskStatus) ?? 'pending';
      const liveStatus = delegatedRunId ? delegatedTaskStatusRegistry.get(delegatedRunId) : undefined;
      const effectiveStatus = liveStatus ?? specStatus;
      return { child, priority: STATUS_PRIORITY[effectiveStatus] ?? 2, index: i };
    });

    // Sort: active first (pending → running → terminal), preserve order within group
    indexed.sort((a, b) => a.priority - b.priority || a.index - b.index);

    // Cap: active cards always shown, terminal cards fill remaining slots
    if (indexed.length > maxCards) {
      const active = indexed.filter((item) => item.priority < 2);
      const terminal = indexed.filter((item) => item.priority >= 2);
      const terminalSlots = Math.max(0, maxCards - active.length);
      // Keep newest terminal cards (highest index = most recently created)
      const keptTerminal = terminal.slice(-terminalSlots);
      return [...active, ...keptTerminal].map((item) => item.child);
    }

    return indexed.map((item) => item.child);
  }, [children, childNodes, node.component.children, maxCards]);

  return (
    <div
      className={`grid ${COLS_CLASS[columns] ?? COLS_CLASS[2]}`}
      style={{ gap: `${gap * 0.25}rem` }}
      data-testid="aui-grid"
    >
      <AnimatePresence initial={false}>
        {visibleChildren.map((child) => (
          <AnimatedListItem key={child.key}>
            {child}
          </AnimatedListItem>
        ))}
      </AnimatePresence>
    </div>
  );
}
